<template>
  <div class="iv-overlay" @click.self="$emit('close')" @wheel.prevent="onWheel">
    <button class="iv-close" @click="$emit('close')" title="Close">&times;</button>

    <button v-if="images.length > 1" class="iv-nav iv-prev" @click="prev">
      <i class="fas fa-chevron-left"></i>
    </button>
    <button v-if="images.length > 1" class="iv-nav iv-next" @click="next">
      <i class="fas fa-chevron-right"></i>
    </button>

    <div class="iv-img-wrap"
      @mousedown="onDragStart"
      @mousemove="onDragMove"
      @mouseup="onDragEnd"
      @mouseleave="onDragEnd"
      @touchstart.prevent="onTouchStart"
      @touchmove.prevent="onTouchMove"
      @touchend="onDragEnd"
    >
      <img
        :src="images[index].src"
        :alt="images[index].name"
        class="iv-img"
        :style="imgStyle"
        draggable="false"
      />
    </div>

    <div class="iv-filename">{{ images[index].name }}</div>
  </div>
</template>

<script>
export default {
  name: 'ImageViewer',
  props: {
    images: { type: Array, required: true },
    startIndex: { type: Number, default: 0 },
  },

  data() {
    return {
      index: this.startIndex,
      scale: 1,
      translateX: 0,
      translateY: 0,
      dragging: false,
      dragStartX: 0,
      dragStartY: 0,
      dragStartTX: 0,
      dragStartTY: 0,
      pinchStartDist: 0,
      pinchStartScale: 1,
    }
  },

  computed: {
    imgStyle() {
      return {
        transform: `scale(${this.scale}) translate(${this.translateX}px, ${this.translateY}px)`,
        cursor: this.scale > 1 ? (this.dragging ? 'grabbing' : 'grab') : 'default',
      }
    },
  },

  mounted() {
    document.addEventListener('keydown', this.onKeydown)
  },

  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeydown)
  },

  methods: {
    onKeydown(e) {
      if (e.key === 'Escape') this.$emit('close')
      else if (e.key === 'ArrowLeft') this.prev()
      else if (e.key === 'ArrowRight') this.next()
    },

    prev() {
      if (this.images.length < 2) return
      this.resetZoom()
      this.index = (this.index - 1 + this.images.length) % this.images.length
    },

    next() {
      if (this.images.length < 2) return
      this.resetZoom()
      this.index = (this.index + 1) % this.images.length
    },

    zoomIn() {
      this.scale = Math.min(this.scale * 1.3, 8)
    },

    zoomOut() {
      this.scale = Math.max(this.scale / 1.3, 0.25)
      if (this.scale <= 1) {
        this.translateX = 0
        this.translateY = 0
      }
    },

    resetZoom() {
      this.scale = 1
      this.translateX = 0
      this.translateY = 0
    },

    onWheel(e) {
      if (e.deltaY < 0) this.zoomIn()
      else this.zoomOut()
    },

    onDragStart(e) {
      if (this.scale <= 1) return
      this.dragging = true
      this.dragStartX = e.clientX
      this.dragStartY = e.clientY
      this.dragStartTX = this.translateX
      this.dragStartTY = this.translateY
    },

    onTouchStart(e) {
      if (e.touches.length === 2) {
        this.pinchStartDist = this.getTouchDist(e.touches)
        this.pinchStartScale = this.scale
        this.dragging = false
        return
      }
      if (this.scale <= 1 || !e.touches.length) return
      this.dragging = true
      this.dragStartX = e.touches[0].clientX
      this.dragStartY = e.touches[0].clientY
      this.dragStartTX = this.translateX
      this.dragStartTY = this.translateY
    },

    getTouchDist(touches) {
      let dx = touches[0].clientX - touches[1].clientX
      let dy = touches[0].clientY - touches[1].clientY
      return Math.sqrt(dx * dx + dy * dy)
    },

    onDragMove(e) {
      if (!this.dragging) return
      let dx = e.clientX - this.dragStartX
      let dy = e.clientY - this.dragStartY
      this.translateX = this.dragStartTX + dx / this.scale
      this.translateY = this.dragStartTY + dy / this.scale
    },

    onTouchMove(e) {
      if (e.touches.length === 2) {
        let dist = this.getTouchDist(e.touches)
        let newScale = this.pinchStartScale * (dist / this.pinchStartDist)
        this.scale = Math.max(0.25, Math.min(newScale, 8))
        if (this.scale <= 1) {
          this.translateX = 0
          this.translateY = 0
        }
        return
      }
      if (!this.dragging || !e.touches.length) return
      let dx = e.touches[0].clientX - this.dragStartX
      let dy = e.touches[0].clientY - this.dragStartY
      this.translateX = this.dragStartTX + dx / this.scale
      this.translateY = this.dragStartTY + dy / this.scale
    },

    onDragEnd() {
      this.dragging = false
    },
  },
}
</script>

<style scoped>
.iv-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.9);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.iv-close {
  position: absolute;
  top: 12px;
  right: 16px;
  background: none;
  border: none;
  color: #fff;
  font-size: 2rem;
  cursor: pointer;
  z-index: 10001;
  line-height: 1;
  padding: 4px 8px;
}
.iv-close:hover { color: #ccc; }

.iv-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.15);
  border: none;
  color: #fff;
  font-size: 1.5rem;
  padding: 12px 14px;
  cursor: pointer;
  z-index: 10001;
  border-radius: 4px;
}
.iv-nav:hover { background: rgba(255, 255, 255, 0.3); }
.iv-prev { left: 12px; }
.iv-next { right: 12px; }

.iv-img-wrap {
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.iv-img {
  max-width: 100vw;
  max-height: 100vh;
  object-fit: contain;
  user-select: none;
  transition: transform 0.1s ease;
}

.iv-filename {
  position: absolute;
  bottom: 12px;
  left: 50%;
  transform: translateX(-50%);
  color: #aaa;
  font-size: 0.8rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 80vw;
}
</style>
